import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import qs from 'query-string';
import { useNavigate, useLocation } from 'react-router-dom';

import './GlobalSearch.styles.scss';
import MacsResults from './MacsResults';
import OtherResults from './OtherResults';
import { getQueryString } from '../../../core/api';
import Modal from '../../../components/Modal/Modal';
import GlobalSearchHeader from './GlobalSearchHeader';
import { GlobalContext } from '../../../GlobalContext';
import RoutesEnum from '../../../core/enums/RoutesEnum';
import { getSlidesPerView } from '../../../core/formatters';
import RecentSearches from './RecentSearches/RecentSearches';
import { iPublication, Publication } from '../../../core/models/Publication';
import { iPublicationClient__API } from '../../../core/apiSchema';
import AiSearchResults from './AiSearchResults/AiSearchResults';
import Scrollbar from '../../../components/Scrollbar/Scrollbar';
import NoResults from '../../../components/NoResults/NoResults';
import NoAiSearchAccess from './AiSearchResults/NoAiSearchAccess';
import { isLetterTyped, isTargetInput } from '../../../core/helpers';
import useSearchResults from '../../../core/apiHooks/useSearchResults';
import useSearchResultsStream from '../../../core/apiHooks/useSearchResultsStream';
import { getAiSectionState, getFirstQueryParam, shouldShowNoResults } from './GlobalSearch.helpers';

/**
 * We intentionally use a large take count to fetch most results in one call.
 * This became necessary after AI Search was introduced.
 * AI Search sources need metadata that the AI Search API does not return.
 * Instead, we parse source IDs from the AI HTML (data-id attributes) and
 * match them to documents returned by the regular search endpoint.
 * Fetching many docs increases the chance that all referenced sources are present.
 */
const DEFAULT_TAKE_COUNT = 150;
export const TAKE_MACS_COUNT = 14;
const SEARCH_TYPE = {
    SIMPLE: 'simple',
    AI: 'ai'
};

function isAllowedRoute(locationPath: string) {
    const DISALLOWED_ROUTES: string[] = [RoutesEnum.Alerts];
    return !DISALLOWED_ROUTES.includes(locationPath);
}

export default function GlobalSearch() {
    const [queryValue, setQueryValue] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');
    const [isSimpleSearchMode, setIsSimpleSearchMode] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const {
        wWidth,
        shouldOpenSearchOnType,
        isSearchOpened,
        setIsSearchOpened,
        inMyLibrary,
        currentUser: { isAiSearchEnabled }
    } = useContext(GlobalContext);

    const slideTilesCount = getSlidesPerView(wWidth - 100, 210);

    const {
        isLoading: isSearchRequestLoading,
        isSuccess: isSearchSuccess,
        data: { macsResults, macsResultsCount, docsResults, docsResultsCount }
    } = useSearchResults(queryValue, inMyLibrary, {
        takeDocs: DEFAULT_TAKE_COUNT,
        takeMacs: TAKE_MACS_COUNT
    });

    const isLoading = isSearchRequestLoading && isSearchSuccess !== false;

    const shouldUseAiSearch = isAiSearchEnabled && !isSimpleSearchMode;

    const {
        isLoading: isStreamLoading,
        htmlContent,
        statusCode,
        error: streamError,
        isSuccess: isStreamSuccess,
        sources
    } = useSearchResultsStream(queryValue, shouldUseAiSearch);

    const aiSourceResults = useMemo<iPublication[]>(() => {
        return sources.map((src) => new Publication({ apiModel: src as iPublicationClient__API }));
    }, [sources]);

    const otherResultsCount = docsResults.length;

    function handleDocumentKeyDown(event: KeyboardEvent) {
        if (!isTargetInput(event) && !isSearchOpened && isAllowedRoute(location.pathname) && isLetterTyped(event)) {
            event.stopPropagation();
            setIsSearchOpened(true);
        }
    }

    const hasResults = otherResultsCount > 0 || macsResultsCount > 0;

    const handleRecentSearchClick = useCallback(
        (search: string) => {
            setSearchInputValue(search);
            setQueryValue(search);
        },
        [setQueryValue, setSearchInputValue]
    );

    /**
     * Handler for AI Search button click.
     * Uses AI search (same as pressing Enter with AI enabled).
     */
    const handleAiSearch = useCallback(() => {
        if (searchInputValue.trim()) {
            setIsSimpleSearchMode(false);
            setQueryValue(searchInputValue);
        }
    }, [searchInputValue]);

    /**
     * Handler for Simple Search button click.
     * Uses only elastic search, skips the AI search service.
     */
    const handleSimpleSearch = useCallback(() => {
        if (searchInputValue.trim()) {
            setIsSimpleSearchMode(true);
            setQueryValue(searchInputValue);
        }
    }, [searchInputValue]);

    useEffect(() => {
        return () => {
            if (isSearchOpened) {
                setTimeout(() => {
                    setQueryValue('');
                    setIsSimpleSearchMode(false);
                }, 300);
            }
        };
    }, [isSearchOpened]);

    useEffect(() => {
        if (!isSearchOpened && shouldOpenSearchOnType) {
            document.addEventListener('keydown', handleDocumentKeyDown, {
                capture: true
            });
        }
        return () => {
            document.removeEventListener('keydown', handleDocumentKeyDown, {
                capture: true
            });
        };
    }, [isSearchOpened, shouldOpenSearchOnType, location]);

    useEffect(() => {
        const handleSearchClick = (event: MouseEvent) => {
            if (isSearchOpened && (event.target as HTMLElement)?.nodeName.toLowerCase() === 'a') {
                const oldSearch = qs.parse(location.search);
                const newSearch = getQueryString({
                    ...oldSearch,
                    q: queryValue,
                    searchType: isSimpleSearchMode ? SEARCH_TYPE.SIMPLE : SEARCH_TYPE.AI
                });

                const url = newSearch ? location.pathname + '?' + newSearch : location.pathname;
                window.history.replaceState(window.history.state, '', url);
            }
        };

        document.addEventListener('click', handleSearchClick, { capture: true });
        return () => {
            document.removeEventListener('click', handleSearchClick, { capture: true });
        };
    }, [isSearchOpened, queryValue, location, isSimpleSearchMode]);

    useEffect(() => {
        const parsed = qs.parse(location.search);
        const searchQuery = getFirstQueryParam(parsed.q);
        const searchType = getFirstQueryParam(parsed.searchType);
        if (searchQuery) {
            setIsSearchOpened(true);
            setQueryValue(searchQuery);
            setSearchInputValue(searchQuery);
            setIsSimpleSearchMode(searchType === SEARCH_TYPE.SIMPLE);
        }
    }, [location.search]);

    useEffect(() => {
        if (isSearchOpened) {
            setIsSearchOpened(false);
        }
    }, [location.pathname]);

    const renderDefaultResults = useMemo(() => {
        return (
            <>
                <OtherResults
                    otherResults={docsResults}
                    isLoading={isLoading}
                    isAiSearchEnabled={isAiSearchEnabled}
                    slideTilesCount={slideTilesCount}
                    otherResultsCount={otherResultsCount}
                    otherResultsStartIndex={0}
                />
                <MacsResults
                    macsResults={macsResults}
                    queryValue={queryValue}
                    isLoading={isLoading}
                    inMyLibrary={inMyLibrary}
                    slideTilesCount={slideTilesCount}
                    macsResultsCount={macsResultsCount}
                />
            </>
        );
    }, [
        docsResults,
        inMyLibrary,
        isAiSearchEnabled,
        isLoading,
        macsResults,
        macsResultsCount,
        otherResultsCount,
        queryValue,
        slideTilesCount
    ]);

    const aiSectionState = getAiSectionState({
        isSimpleSearchMode,
        isAiSearchEnabled,
        statusCode,
        error: streamError,
        htmlContent,
        isStreamLoading
    });
    const showNoResults = shouldShowNoResults({ isSearchLoading: isLoading, hasResults, aiSectionState });

    const renderAiSection = useMemo(() => {
        if (aiSectionState === 'hidden') {
            return null;
        }

        if (aiSectionState === 'no-access') {
            return <NoAiSearchAccess />;
        }

        return (
            <AiSearchResults
                htmlContent={htmlContent}
                sources={aiSourceResults}
                statusCode={statusCode}
                isSuccess={isStreamSuccess}
                isAnswerLoading={aiSectionState === 'loading'}
                isSourcesLoading={isStreamLoading || isLoading}
            />
        );
    }, [aiSectionState, aiSourceResults, htmlContent, isLoading, isStreamLoading, isStreamSuccess, statusCode]);

    const renderResults = useMemo(() => {
        return (
            <>
                {renderAiSection}
                {showNoResults ? (
                    <NoResults
                        title={'Sorry, no results for this search'}
                        subTitle={'Please try again, you can search words like pizza, burger, breakfast, etc'}
                        isAbsolute
                    />
                ) : (
                    renderDefaultResults
                )}
            </>
        );
    }, [renderAiSection, renderDefaultResults, showNoResults]);

    return (
        <Modal
            isOpened={isSearchOpened}
            close={() => {
                let search = qs.parse(location.search || '');
                delete search.q;
                delete search.searchType;
                void navigate({ search: getQueryString(search) }, { replace: true });
                setSearchInputValue('');
                setIsSearchOpened(false);
            }}
            hasInner={false}
            closeOnEsc
            animationName="slide-top"
            className="is-global-search-modal"
        >
            <div className="GlobalSearch">
                <GlobalSearchHeader
                    setQueryValue={setQueryValue}
                    setSearchInputValue={setSearchInputValue}
                    searchInputValue={searchInputValue}
                    isAiSearchEnabled={isAiSearchEnabled}
                    onAiSearch={handleAiSearch}
                    onSimpleSearch={handleSimpleSearch}
                />

                {queryValue.trim() === '' ? (
                    <RecentSearches onSearch={handleRecentSearchClick} />
                ) : (
                    <div className="GlobalSearch__results">
                        <Scrollbar>
                            <div className="GlobalSearch__results-inner">{renderResults}</div>
                        </Scrollbar>
                    </div>
                )}
            </div>
        </Modal>
    );
}
